/**
 * 把 zod 的驗證錯誤變成「使用者找得到是哪一格」的訊息(REQ-20260818003)。
 *
 * `packages/shared/src/zod-error-map.ts` 給的是**全域預設訊息**(「型別錯誤」「必填」),
 * 刻意不帶欄位名 —— 同一條規則會用在後端、其他表單、API 回應上。
 * 結果表單直接印 `issues[0].message` 時,畫面上就只有一句「型別錯誤」,
 * 使用者得自己一格一格找。這裡補的是**顯示層**的欄位定位,不動那份預設訊息。
 *
 * 欄位標籤刻意由呼叫端傳進來,也刻意**不**放進 `@app/shared`:
 * 標籤是「這張表單畫面上寫什麼」,同一個 `qty` 在進貨單叫「數量」、在別處可能叫別的;
 * 放共用套件等於要維護一份跟後端無關、又對不上任何單一畫面的字典。
 */
import type { ZodError } from 'zod';

/**
 * 欄位路徑 → 畫面上的中文標籤。
 * 陣列元素用 `#` 代表索引,例如品項的數量是 `'lines.#.qty'`。
 */
export type FieldLabelMap = Record<string, string>;

export function describeZodError(
  error: ZodError,
  labels: FieldLabelMap,
  fallback = '欄位格式不正確',
): string {
  const issue = error.issues[0];
  if (!issue) return fallback;

  return describeFieldIssue(issue.path, issue.message, labels, error.issues.length - 1);
}

/**
 * 單一欄位錯誤的句型。前端 zod 驗證與後端回的 `error.fields`(REQ-20260929001)共用,
 * 使用者不論錯在哪一關,看到的都是同一種「第 N 筆品項的「數量」:…」。
 *
 * @param moreCount 除了這一個之外還有幾個欄位出錯
 */
export function describeFieldIssue(
  path: readonly (string | number)[],
  message: string,
  labels: FieldLabelMap,
  moreCount: number,
): string {
  // 一次只指第一個錯,其餘只報數量:全部列出來會變成一大段紅字,反而更難讀
  const more = moreCount > 0 ? `(另有 ${moreCount} 個欄位待修正)` : '';

  const last = path[path.length - 1];
  // 整張表單層級的 issue(path 是空的)沒有欄位可指,原樣顯示訊息
  if (last === undefined) return `${message}${more}`;

  const key = path.map((seg) => (typeof seg === 'number' ? '#' : seg)).join('.');
  // 查不到就退回欄位名本身 —— 是英文,但至少比沒有線索好
  const label = labels[key] ?? String(last);
  // 品項是陣列,只講「數量」分不出是哪一列
  const prefix =
    path[0] === 'lines' && typeof path[1] === 'number' ? `第 ${path[1] + 1} 筆品項的` : '';

  return `${prefix}「${label}」:${message}${more}`;
}
